/**
 * withChartConfiguration - Higher-Order Component for ECharts
 * Following ROSIE principle: Solve Once, Reuse Forever
 *
 * This HOC provides:
 * - Automatic chart initialization and disposal
 * - Resize event handling
 * - Export functionality (PNG, Email, Social)
 * - Loading states
 * - Error boundaries
 * - Consistent theming
 */

import React, { useRef, useEffect, useState, ComponentType } from 'react';
import { getECharts, loadChart } from '../services/EChartsLoader';
import { ChartSizingUtils } from '../utils/ChartSizingUtils';
import { EChartsOption } from 'echarts';
import { log } from '../services/LoggingService';
import {
  downloadChart,
  getChartDimensions,
  AspectRatio
} from '../utils/chartUtils';
import { FilenameUtils } from '../utils/filenameUtils';

export interface ChartProps {
  data: any;
  title?: string;
  subtitle?: string;
  timeframe?: string;
  weekOffset?: number;
  timeGrouping?: string;
  chartContext?: string;
  chartType?: string;
  aspectRatio?: AspectRatio;
  height?: number;
  className?: string;
  yearVisibility?: {
    show2023: boolean;
    show2024: boolean;
    show2025: boolean;
  };
  filters?: Record<string, string[] | undefined>;
  onLegendSelectChanged?: (selected: Record<string, boolean>) => void;
}

export interface EnhancedChartProps extends ChartProps {
  chartRef: React.RefObject<HTMLDivElement>;
  chartInstance: React.MutableRefObject<echarts.ECharts | null>;
  isLoading: boolean;
  error: Error | null;
  downloadChart: (type: 'png' | 'email' | 'social' | 'blog') => void;
  refreshChart: () => void;
}

/**
 * Higher-Order Component that wraps chart components with ECharts functionality
 * Handles all boilerplate: initialization, disposal, resize, exports
 */
export function withChartConfiguration<P extends ChartProps>(
  ChartComponent: ComponentType<P & EnhancedChartProps>,
  getChartOption: (props: P) => EChartsOption
) {
  return function WrappedChart(props: P) {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstance = useRef<echarts.ECharts | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    // Initialize and update chart
    useEffect(() => {
      const initializeChart = async () => {
        if (!chartRef.current || !props.data) return;

        try {
          setIsLoading(true);
          setError(null);

          // Dispose existing chart
          if (chartInstance.current) {
            chartInstance.current.dispose();
            chartInstance.current = null;
          }

          // Clear DOM for clean slate
          if (chartRef.current) {
            chartRef.current.innerHTML = '';
          }

          // Load chart type if specified
          if (props.chartType) {
            await loadChart(props.chartType);
          }

          // Get ECharts instance
          const echarts = getECharts();

          // Get optimal sizing based on chart type and context
          const chartType = props.chartType || 'bar';
          const dataPoints = props.data?.length || 10;
          const sizingOptions = {
            chartType: chartType as any,
            dataPoints,
            context: 'blog' as any, // Optimize for blog/email readability
            maxWidth: 1400,
            maxHeight: 800
          };

          const initOptions = ChartSizingUtils.getEChartsInitOptions(sizingOptions);
          
          // Create new chart instance with optimal sizing
          chartInstance.current = echarts.init(chartRef.current, null, initOptions);

          // Get configuration from the provided function (includes timeGrouping)
          const option = getChartOption(props);

          // Apply optimal sizing to the chart configuration
          const sizedOption = ChartSizingUtils.getSizedChartConfig(option, sizingOptions);

          // Apply configuration with proper options
          chartInstance.current.setOption(sizedOption, {
            notMerge: true,
            lazyUpdate: false
          });

          // Force resize to ensure proper dimensions
          setTimeout(() => {
            if (chartInstance.current && !chartInstance.current.isDisposed()) {
              chartInstance.current.resize();
            }
          }, 100);

          // Add legend select change handler if provided
          if (props.onLegendSelectChanged) {
            // Remove any existing legend event handlers first
            chartInstance.current.off('legendselectchanged');

            // Add new handler
            chartInstance.current.on('legendselectchanged', (params: any) => {
              log.debug('Legend selection changed:', params.selected, "Debug");
              props.onLegendSelectChanged!(params.selected);
            });
          }

          setIsLoading(false);
        } catch (err) {
          console.error('Chart initialization error details:', {
            error: err,
            errorMessage: err instanceof Error ? err.message : 'Unknown error',
            errorStack: err instanceof Error ? err.stack : 'No stack trace',
            chartData: props.data,
            timeframe: props.timeframe,
            timeGrouping: props.timeGrouping
          });
          log.error('Chart initialization error:', "Chart", err);
          setError(err instanceof Error ? err : new Error('Chart initialization failed'));
          setIsLoading(false);
        }
      };

      initializeChart();
    }, [props.data, props.timeframe, props.timeGrouping, props.onLegendSelectChanged]); // Re-render when data, timeframe, grouping, or handler changes

    // Handle window resize
    useEffect(() => {
      const handleResize = () => {
        if (chartInstance.current && !chartInstance.current.isDisposed()) {
          chartInstance.current.resize();
        }
      };

      window.addEventListener('resize', handleResize);

      // Initial resize to fit container
      setTimeout(handleResize, 100);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (chartInstance.current && !chartInstance.current.isDisposed()) {
          chartInstance.current.dispose();
          chartInstance.current = null;
        }
      };
    }, []);

    // Download functionality with optimal dimensions and improved filenames
    // Memoize to prevent unnecessary re-creation and avoid triggering download on every render
    const downloadChartHandler = React.useCallback((type: 'png' | 'email' | 'social' | 'blog') => {
      if (!chartInstance.current || chartInstance.current.isDisposed()) {
        console.error('Chart instance not available for download');
        return;
      }

      // Generate improved filename based on chart title and context
      const filename = FilenameUtils.generateChartFilename(
        props.title || 'Chart',
        props.timeframe || 'custom',
        type === 'png' ? 'blog' : type
      );

      // Use the single universal download function
      downloadChart(chartInstance.current, filename, type === 'png' ? 'blog' : type);
    }, [props.title, props.timeframe]);

    // Refresh chart
    const refreshChart = () => {
      if (chartInstance.current && !chartInstance.current.isDisposed()) {
        const option = getChartOption(props);
        chartInstance.current.setOption(option, {
          notMerge: true,
          lazyUpdate: false
        });
      }
    };

    // Enhanced props to pass to wrapped component
    const enhancedProps: P & EnhancedChartProps = {
      ...props,
      chartRef,
      chartInstance,
      isLoading,
      error,
      downloadChart: downloadChartHandler,
      refreshChart
    };

    return <ChartComponent {...enhancedProps} />;
  };
}

/**
 * Custom hook for chart operations
 * Use this when you need more control than the HOC provides
 */
export function useChartOperations(
  chartInstance: React.MutableRefObject<echarts.ECharts | null>
) {
  const exportAsPNG = (filename?: string) => {
    if (!chartInstance.current || chartInstance.current.isDisposed()) {
      console.error('Chart instance not available');
      return;
    }

    const defaultFilename = `chart-${new Date().toISOString().split('T')[0]}.png`;
    downloadChart(chartInstance.current, filename || defaultFilename, 'blog');
  };

  const updateOption = (option: EChartsOption, merge: boolean = false) => {
    if (!chartInstance.current || chartInstance.current.isDisposed()) {
      console.error('Chart instance not available');
      return;
    }

    chartInstance.current.setOption(option, {
      notMerge: !merge,
      lazyUpdate: false
    });
  };

  const getDataURL = (type: 'png' | 'jpeg' | 'svg' = 'png'): string | null => {
    if (!chartInstance.current || chartInstance.current.isDisposed()) {
      return null;
    }

    return chartInstance.current.getDataURL({
      type,
      pixelRatio: 3, // Higher DPI for better quality exports
      backgroundColor: '#ffffff'
    });
  };

  return {
    exportAsPNG,
    updateOption,
    getDataURL
  };
}